'use client';

import { IContactItem } from '@/types/layout';
import { faEnvelope, faLink, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

const _getIcon = (url: string, classes?: string) => {
  switch (true) {
    case url.includes('tel:'):
      return <FontAwesomeIcon icon={faPhone} className={classes} />;
    case url.includes('mailto:'):
      return <FontAwesomeIcon icon={faEnvelope} className={classes} />;
    default:
      return <FontAwesomeIcon icon={faLink} className={classes} />;
  }
};

export interface IHeaderContactProps {
  contactItems: IContactItem[];
}

export const HeaderContact = ({ contactItems }: IHeaderContactProps) => {
  return (
    <Fragment>
      {contactItems.map((item, index) => (
        <a
          href={item.url}
          key={item.title + index}
          className={twMerge('flex flex-col gap-1 max-xl:hidden', item.classes)}
          data-testid={`${item.title}-header-link`}
        >
          <span>{item.title}</span>
          <div className="flex gap-2 items-center">
            {_getIcon(item.url, 'size-3.5')}
            <span>{item.text}</span>
          </div>
        </a>
      ))}
    </Fragment>
  );
};

export interface IHeaderContactMobileProps {
  contactItems: IContactItem[];
}

export const HeaderContactMobile = ({ contactItems }: IHeaderContactMobileProps) => {
  return (
    <Fragment>
      {contactItems.map((item, index) => (
        <a href={item.url} key={item.title + index} className={twMerge('xl:hidden', item.classes)}>
          {_getIcon(item.url, 'size-5 md:size-4 block')}
        </a>
      ))}
    </Fragment>
  );
};

export interface ICustomHeaderContactProps {
  contact_items: IContactItem[];
  customContactsPortalTargetId?: string;
  customMobileContactPortalTargetId?: string;
}

export const CustomHeaderContact = ({
  contact_items,
  customContactsPortalTargetId,
  customMobileContactPortalTargetId,
}: ICustomHeaderContactProps) => {
  const [contactsPortalTarget, setContactsPortalTarget] = useState<Element | null>(null);
  const [mobileContactPortalTarget, setMobileContactPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    setContactsPortalTarget(document.getElementById(customContactsPortalTargetId ?? 'header-contacts-portal-target'));
    setMobileContactPortalTarget(
      document.getElementById(customMobileContactPortalTargetId ?? 'header-mobile-contact-portal-target'),
    );
  }, []);

  return (
    <Fragment>
      {contactsPortalTarget &&
        createPortal(
          <Fragment>
            <HeaderContact contactItems={contact_items} />
          </Fragment>,
          contactsPortalTarget,
        )}

      {mobileContactPortalTarget &&
        createPortal(
          <HeaderContactMobile contactItems={contact_items.filter((item) => item.show_on_mobile)} />,
          mobileContactPortalTarget,
        )}
    </Fragment>
  );
};
